import { CardComponentProps, Variant } from "@~/types/types";
import styles from "./ColourSwatches.module.scss";
import classNames from "classnames/bind";
import { useVariant } from "@~/contexts/VariantContext/VariantContext";
import { useFeatureFlags } from "@~/flags/flags";
import { useRef } from "react";
import { dispatchAdTechAnalyticsEvent } from "@~/utils/analytics";

const cx = classNames.bind(styles);

function getUniqueColourVariantsWithSwatches(variants: Variant[]): Variant[] {
  return variants.filter((variant, index, arr) => {
    return (
      "colourSwatch" in variant &&
      arr.findIndex(
        (v: Variant) =>
          v.differentiators?.colour === variant.differentiators?.colour,
      ) === index
    );
  });
}

export function ColourSwatches({
  product,
}: Pick<CardComponentProps, "product">) {
  const { variants } = product;
  const { variant: selectedVariant, setSelectedColour } = useVariant();

  const colourSwatchVariants = getUniqueColourVariantsWithSwatches(variants);

  const swatchListRef = useRef<HTMLDivElement>(null);
  const { isShowNumberOfSwatchesEnabled } = useFeatureFlags();

  const showNumberOfSwatches = isShowNumberOfSwatchesEnabled.value;
  const maxSwatchesToDisplay = 4;
  const notDisplayedTotal = colourSwatchVariants.length - maxSwatchesToDisplay;

  if (colourSwatchVariants.length < 2) {
    return null;
  }

  const swatchesToDisplay = showNumberOfSwatches
    ? colourSwatchVariants.slice(0, maxSwatchesToDisplay)
    : colourSwatchVariants;

  const adTechId = product?.advertising?.associatedAdvertId;

  const handleClick = (colour: string) => {
    setSelectedColour(colour);
    if (adTechId) {
      dispatchAdTechAnalyticsEvent(product.aliases.pimProductId, adTechId);
    }
  };

  return (
    <div
      className={cx(styles.container, {
        ["container--withTotal"]: showNumberOfSwatches,
      })}
      data-component="ColourSwatches"
      ref={swatchListRef}
    >
      <div className={styles.list}>
        {swatchesToDisplay.map((variant: Variant) => {
          if (!("colourSwatch" in variant) || !variant.colourSwatch) {
            return null;
          }
          return (
            <button
              type="button"
              key={variant.id}
              title={variant.differentiators?.colour || ""}
              className={cx("button", {
                "button--unavailable": !variant.availability?.availableToOrder,
                "button--selected": variant.id === selectedVariant?.id,
              })}
              onClick={() => {
                handleClick(variant.differentiators?.colour || "");
              }}
            >
              <img
                src={`${variant.colourSwatch}${variant.colourSwatch.includes("?") ? "&" : "?"}wid=18&hei=18&fit=crop&sm=c`}
                className={styles.image}
                alt={`${variant.differentiators?.colour}${
                  variant.availability?.availableToOrder
                    ? ""
                    : " - Out of stock"
                }`}
              />
              <span className={styles.visuallyHidden}>
                {`${variant.differentiators?.colour} is ${
                  variant.availability?.availableToOrder
                    ? "available"
                    : "unavailable"
                }`}
              </span>
            </button>
          );
        })}
      </div>
      {showNumberOfSwatches && notDisplayedTotal > 0 && (
        <a
          data-testid="colour-swatch-more-available-total"
          className={cx("total")}
          href={selectedVariant?.pdpURL.url}
        >{`+ ${notDisplayedTotal}`}</a>
      )}
      {!showNumberOfSwatches && (
        <a className={styles.moreColours} href={selectedVariant?.pdpURL.url}>
          + More colours available
        </a>
      )}
    </div>
  );
}
